/* eslint-disable i18next/no-literal-string */

import { Box, Theme, Typography } from "@outschool/backpack";
import React from "react";

import {
  AGE_RANGE_OPTIONS,
  CREATURE_TYPES,
  GENDER_OPTIONS,
  HAIR_COLORS,
  SKIN_COLORS,
} from "./avatar-builder-constants";
import { ChipButton, ColorSwatch } from "./AvatarBuilderFormControls";

export interface AvatarBuilderTypeTabProps {
  creatureType: string;
  setCreatureType: React.Dispatch<React.SetStateAction<string>>;
  gender: string;
  setGender: React.Dispatch<React.SetStateAction<string>>;
  ageRange: string;
  setAgeRange: React.Dispatch<React.SetStateAction<string>>;
  hairColor: string;
  setHairColor: React.Dispatch<React.SetStateAction<string>>;
  skinColor: string;
  setSkinColor: React.Dispatch<React.SetStateAction<string>>;
}

export function AvatarBuilderTypeTab({
  creatureType,
  setCreatureType,
  gender,
  setGender,
  ageRange,
  setAgeRange,
  hairColor,
  setHairColor,
  skinColor,
  setSkinColor,
}: AvatarBuilderTypeTabProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: "0px 5px",
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ fontSize: "1.5rem", fontWeight: 600 }}
      >
        Avatar type
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 8,
          minWidth: 0,
        }}
      >
        {CREATURE_TYPES.map(ct => (
          <ChipButton
            key={ct.value}
            label={ct.label}
            icon={ct.icon}
            selected={creatureType === ct.value}
            onClick={() => setCreatureType(ct.value)}
          />
        ))}
      </Box>

      {creatureType === "human" && (
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 15 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              columnGap: 8,
              rowGap: 0,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                gridColumn: { xs: "1 / -1", lg: "1 / 4" },
                gridRow: 1,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ fontSize: "1.5rem", fontWeight: 600, mb: 10 }}
              >
                Gender (optional)
              </Typography>
            </Box>

            {GENDER_OPTIONS.map((opt, index) => (
              <Box
                key={opt.value}
                sx={{
                  gridColumn: index + 1,
                  gridRow: { xs: 2, lg: 2 },
                  minWidth: 0,
                  width: "100%",
                  "& > *": { width: "100%" },
                }}
              >
                <ChipButton
                  label={opt.label}
                  selected={gender === opt.value}
                  onClick={() => setGender(opt.value)}
                />
              </Box>
            ))}
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontSize: "1.5rem", fontWeight: 600, mb: 10 }}
            >
              Age range (optional)
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                gap: 8,
                minWidth: 0,
              }}
            >
              {AGE_RANGE_OPTIONS.map(opt => (
                <ChipButton
                  key={opt.value}
                  label={opt.label}
                  selected={ageRange === opt.value}
                  onClick={() =>
                    setAgeRange(prev => (prev === opt.value ? "" : opt.value))
                  }
                />
              ))}
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              paddingRight: { xs: "5px", md: "10px" },
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontSize: "1.5rem", fontWeight: 600, mb: 0.25 }}
            >
              Appearance (optional)
            </Typography>
            <Typography
              variant="caption"
              sx={(theme: Theme) => ({
                color: theme.palette.text.secondary,
                display: "block",
                mb: 10,
              })}
            >
              Make it look more like you
            </Typography>

            <Typography variant="body2" sx={{ fontWeight: 600, mb: 10 }}>
              Hair color
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, 32px)",
                justifyContent: "left",
                gap: 30,
                mb: 10,
                minWidth: 0,
              }}
            >
              {HAIR_COLORS.map(color => (
                <ColorSwatch
                  key={color}
                  color={color}
                  selected={hairColor === color}
                  onClick={() =>
                    setHairColor(prev => (prev === color ? "" : color))
                  }
                />
              ))}
            </Box>

            <Typography variant="body2" sx={{ fontWeight: 600, mb: 10 }}>
              Skin color
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, 32px)",
                justifyContent: "left",
                gap: 25,
                minWidth: 0,
              }}
            >
              {SKIN_COLORS.map(color => (
                <ColorSwatch
                  key={color}
                  color={color}
                  selected={skinColor === color}
                  onClick={() =>
                    setSkinColor(prev => (prev === color ? "" : color))
                  }
                />
              ))}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
